import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, Send, Stamp, ScrollText, FileText } from "lucide-react";
import api, { formatApiError } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { track } from "../analytics";

export const SERVICE_OPTIONS = [
  ["Garvi 2.0 Assistance", "Garvi 2.0 સહાય"],
  ["E-Stamping", "ઈ-સ્ટેમ્પિંગ"],
  ["Rent Agreement", "ભાડા કરાર"],
  ["Index-2 / Certified Copy", "Index-2 / પ્રમાણિત નકલ"],
  ["7/12 / 8-A / Property Card", "7/12 / 8-અ / પ્રોપર્ટી કાર્ડ"],
  ["Property Verification", "પ્રોપર્ટી વેરિફિકેશન"],
  ["Other", "અન્ય"],
];

export function waLink(number, message) {
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

export function LeadForm({ defaultService = "Garvi 2.0 Assistance", compact = false }) {
  const { lang, site } = useApp();
  const gu = lang === "gu";
  const [form, setForm] = useState({ name: "", mobile: "", service: defaultService, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source_page: window.location.pathname, lang });
      track("lead_form_submit", { service: form.service });
      setDone(true);
      toast.success(gu ? "આભાર! અમારી ટીમ ટૂંક સમયમાં WhatsApp/કોલ પર સંપર્ક કરશે." : "Thank you! Our team will contact you shortly.");
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  const input = "w-full h-11 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  if (done) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="lead-form-success">
        <p className="font-display font-bold text-lg text-emerald-800">{gu ? "વિનંતી મળી ગઈ!" : "Request received!"}</p>
        <p className="text-sm text-emerald-700 mt-1">{gu ? "અમારી ટીમ ટૂંક સમયમાં તમારો સંપર્ક કરશે." : "Our team will contact you shortly."}</p>
        {site.whatsapp && (
          <a href={waLink(site.whatsapp, gu ? `નમસ્તે DocGalaxy, મેં ${form.service} માટે ફોર્મ ભર્યું છે.` : `Hi DocGalaxy, I submitted a request for ${form.service}.`)}
            target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { placement: "lead_success" })}
            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-emerald-800 underline" data-testid="lead-success-whatsapp">
            <MessageCircle size={16} /> {gu ? "WhatsApp પર તરત વાત કરો" : "Chat on WhatsApp now"}
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`bg-white border border-border rounded-xl ${compact ? "p-4" : "p-6"} space-y-3`} data-testid="lead-form">
      <div>
        <p className="font-display font-bold text-lg">{gu ? "કોલબેક / સહાય માટે વિનંતી કરો" : "Request a callback / assistance"}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{gu ? "નામ અને મોબાઇલ નંબર આપો — અમે WhatsApp/કોલ પર સંપર્ક કરીશું." : "Share your name and mobile — we'll reach you on WhatsApp/call."}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={input} required placeholder={gu ? "તમારું નામ *" : "Your name *"} value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" />
        <input className={input} required inputMode="numeric" placeholder={gu ? "મોબાઇલ (WhatsApp) *" : "Mobile (WhatsApp) *"} value={form.mobile}
          onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="lead-mobile" />
      </div>
      <select className={input} value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="lead-service">
        {SERVICE_OPTIONS.map(([en, g]) => <option key={en} value={en}>{gu ? g : en}</option>)}
      </select>
      <textarea className={`${input} h-20 py-2`} placeholder={gu ? "સંદેશ (વૈકલ્પિક)" : "Message (optional)"} value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="lead-message" />
      <Button type="submit" className="w-full h-11 bg-primary hover:bg-primary/90" disabled={loading} data-testid="lead-submit-btn">
        <Send size={16} className="mr-1.5" /> {loading ? (gu ? "મોકલી રહ્યા છીએ..." : "Sending...") : (gu ? "વિનંતી મોકલો" : "Send Request")}
      </Button>
      <p className="text-[11px] text-muted-foreground">{gu ? "DocGalaxy એક ખાનગી સહાય સેવા છે, સરકારી પોર્ટલ નથી." : "DocGalaxy is a private assistance service, not a government portal."}</p>
    </form>
  );
}

export function ServiceCTA({ service = "Garvi 2.0 Assistance", title, subtitle, whatsappText, showForm = true, related = [] }) {
  const { lang, site } = useApp();
  const gu = lang === "gu";
  const wa = site.whatsapp;
  const phone = site.phone;
  const msg = whatsappText || (gu ? `નમસ્તે DocGalaxy, મને ${service} માટે સહાય જોઈએ છે.` : `Hi DocGalaxy, I need help with ${service}.`);
  const links = related.length ? related : [
    ["/e-stamping", Stamp, gu ? "ઈ-સ્ટેમ્પિંગ સેવા" : "E-Stamp Service"],
    ["/rent-agreement", ScrollText, gu ? "ગુજરાતી ભાડા કરાર" : "Gujarati Rent Agreement"],
    ["/services", FileText, gu ? "Index-2 / Certified Copy" : "Index-2 / Certified Copy"],
  ];

  return (
    <section className="mt-10 rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/5 to-secondary/5 p-6 md:p-8" data-testid="service-cta">
      <div className={`grid gap-6 ${showForm ? "lg:grid-cols-2" : ""}`}>
        <div>
          <h2 className="font-display font-bold text-xl md:text-2xl">{title || (gu ? `${service} માં મદદ જોઈએ છે?` : `Need help with ${service}?`)}</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {subtitle || (gu ? "DocGalaxy ની રાજકોટ ટીમ ગુજરાતી + English માં માર્ગદર્શન આપે છે. WhatsApp કરો, કોલ કરો અથવા ફોર્મ ભરો." : "DocGalaxy's Rajkot team guides you in Gujarati + English. WhatsApp, call, or fill the form.")}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {wa && (
              <a href={waLink(wa, msg)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { placement: "service_cta", service })}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors" data-testid="cta-whatsapp-btn">
                <MessageCircle size={18} /> WhatsApp
              </a>
            )}
            {phone && (
              <a href={`tel:${phone}`} onClick={() => track("phone_click", { placement: "service_cta", service })}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-primary hover:bg-primary/90 text-white text-sm font-semibold transition-colors" data-testid="cta-call-btn">
                <Phone size={18} /> {gu ? "કોલ કરો" : "Call"} {phone}
              </a>
            )}
            <Button variant="outline" className="h-11" asChild data-testid="cta-contact-btn"><Link to="/contact">{gu ? "સંપર્ક પેજ" : "Contact page"}</Link></Button>
          </div>
          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{gu ? "અમારી સેવાઓ" : "Our services"}</p>
            <div className="flex flex-wrap gap-2">
              {links.map(([to, Icon, label]) => (
                <Link key={to} to={to} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary bg-white border border-border rounded-full px-3 py-1.5 hover:border-primary transition-colors" data-testid={`cta-link-${to.replace(/\//g, "") || "home"}`}>
                  <Icon size={14} /> {label}
                </Link>
              ))}
            </div>
          </div>
        </div>
        {showForm && <LeadForm defaultService={service} />}
      </div>
    </section>
  );
}
